import { FC, ReactNode, useState } from 'react';
import {
    Box,
    Drawer,
    List,
    Typography,
    IconButton,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Tooltip,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard,
    LibraryBooks,
    MenuBook,
    FolderSpecial,
    People,
    Notifications,
    School,
    AutoStories,
    SmartToy,
    WorkHistory,
    ReportProblem,
    Storefront,
    Campaign,
    SystemUpdate,
    SupportAgent,
    Settings,
    BugReport,
    Tune,
    TrendingUp,
    AttachMoney,
    ExpandLess,
    ExpandMore,
    ChevronLeft,
    ChevronRight,
    RecordVoiceOver,
    AutoAwesome,
    Pets,
    EmojiEvents,
    FactCheck,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const drawerWidth = 248;
const drawerCollapsedWidth = 68;

const colors = {
    bg: '#1a2332',
    text: '#94a3b8',
    textHover: '#cbd5e1',
    active: '#3b82f6',
    activeBg: 'rgba(59, 130, 246, 0.15)',
    sectionLabel: '#64748b',
    divider: 'rgba(148, 163, 184, 0.12)',
};

interface MenuItem {
    title: string;
    icon: React.ReactNode;
    path?: string;
    children?: { title: string; path: string }[];
}

interface MenuSection {
    label: string;
    items: MenuItem[];
}

const menuSections: MenuSection[] = [
    {
        label: '개요',
        items: [
            { title: '대시보드', icon: <Dashboard />, path: '/dashboard' },
        ],
    },
    {
        label: '콘텐츠 관리',
        items: [
            {
                title: '라이브러리', icon: <LibraryBooks />, children: [
                    { title: '목록', path: '/library' },
                    { title: '통계', path: '/library/stats' },
                    { title: '섹션 관리', path: '/library/sections' },
                ],
            },
            {
                title: '단어장 그룹', icon: <FolderSpecial />, children: [
                    { title: '목록', path: '/wordbook-group' },
                    { title: '통계', path: '/wordbook-group/stats' },
                ],
            },
            {
                title: '단어장', icon: <MenuBook />, children: [
                    { title: '목록', path: '/wordbook' },
                    { title: '통계', path: '/wordbook/stats' },
                ],
            },
            { title: '사전 단어 관리', icon: <AutoStories />, path: '/dictionary' },
            { title: '사전 검증 리뷰', icon: <FactCheck />, path: '/dictionary/review' },
            { title: 'AI 이미지 생성', icon: <AutoAwesome />, path: '/ai-images' },
            {
                title: '에삐 스티커북', icon: <Pets />, children: [
                    { title: '코스튬 목록', path: '/eppi-costumes' },
                    { title: '카테고리 관리', path: '/eppi-categories' },
                ],
            },
        ],
    },
    {
        label: '학습 운영',
        items: [
            { title: '미션', icon: <EmojiEvents />, path: '/missions' },
            {
                title: 'TTS 음성 관리', icon: <RecordVoiceOver />, children: [
                    { title: '큐/생성 현황', path: '/tts-management' },
                    { title: 'Voice 관리', path: '/tts-voice-config' },
                ],
            },
        ],
    },
    {
        label: '운영',
        items: [
            { title: '사용자', icon: <People />, path: '/user' },
            { title: '신고 관리', icon: <ReportProblem />, path: '/reports' },
            { title: '고객문의', icon: <SupportAgent />, path: '/inquiries' },
            {
                title: '푸시 관리', icon: <Notifications />, children: [
                    { title: '발송 이력', path: '/push' },
                    { title: '로컬 푸시 스케줄', path: '/local-push' },
                ],
            },
            { title: '공지사항', icon: <Campaign />, path: '/notices' },
        ],
    },
    {
        label: '통계',
        items: [
            { title: '사용자 활동', icon: <TrendingUp />, path: '/analytics/user-activity' },
            { title: '매출/구독', icon: <AttachMoney />, path: '/analytics/revenue' },
            { title: '학습 콘텐츠', icon: <School />, path: '/analytics/learning' },
            { title: 'AI 운영', icon: <SmartToy />, path: '/analytics/ai-operations' },
        ],
    },
    {
        label: '비즈니스',
        items: [
            { title: 'IAP 상품', icon: <Storefront />, path: '/iap-products' },
            { title: '구독 제한', icon: <Tune />, path: '/subscription-limits' },
        ],
    },
    {
        label: '시스템',
        items: [
            { title: '배치 작업', icon: <WorkHistory />, path: '/batch/jobs' },
            { title: 'AI 요청 로그', icon: <SmartToy />, path: '/ai/request-logs' },
            { title: 'AI 검증 요약', icon: <FactCheck />, path: '/ai/validation' },
            { title: '버전 관리', icon: <SystemUpdate />, path: '/app-versions' },
            { title: '예외 로그', icon: <BugReport />, path: '/exceptions' },
            { title: '시스템 설정', icon: <Settings />, path: '/system-settings' },
        ],
    },
];

interface Props {
    children: ReactNode;
}

export const DashboardLayout: FC<Props> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(true);
    const [expandedMenu, setExpandedMenu] = useState<string | null>(null);

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

    const handleDesktopDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
        if (drawerOpen) setExpandedMenu(null);
    };

    const isPathMatch = (basePath: string): boolean => {
        return location.pathname === basePath || location.pathname.startsWith(`${basePath}/`);
    };

    const isActive = (item: MenuItem): boolean => {
        if (item.path) return isPathMatch(item.path);
        return item.children?.some(c => isPathMatch(c.path)) ?? false;
    };

    const isChildActive = (path: string): boolean => location.pathname === path;

    const handleItemClick = (item: MenuItem) => {
        if (item.path) {
            navigate(item.path);
            setMobileOpen(false);
            return;
        }
        if (item.children) {
            if (!drawerOpen) {
                navigate(item.children[0].path);
                return;
            }
            setExpandedMenu(expandedMenu === item.title ? null : item.title);
        }
    };

    const drawerContent = (isDesktop: boolean) => (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: colors.bg }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isDesktop && !drawerOpen ? 'center' : 'space-between',
                px: isDesktop && !drawerOpen ? 1 : 2.5,
                py: 2,
                minHeight: 56,
            }}>
                {(!isDesktop || drawerOpen) && (
                    <Typography
                        variant="h6"
                        noWrap
                        sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-0.01em' }}
                    >
                        AI-Voca Admin
                    </Typography>
                )}
                {isDesktop ? (
                    <IconButton
                        onClick={handleDesktopDrawerToggle}
                        size="small"
                        sx={{ color: colors.text, '&:hover': { color: colors.textHover } }}
                    >
                        {drawerOpen ? <ChevronLeft /> : <ChevronRight />}
                    </IconButton>
                ) : null}
            </Box>

            {/* Menu */}
            <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
                {menuSections.map((section, sectionIdx) => (
                    <Box key={section.label}>
                        {sectionIdx > 0 && (
                            <Box sx={{ mx: 2, my: 1, borderTop: `1px solid ${colors.divider}` }} />
                        )}
                        {(drawerOpen || !isDesktop) && (
                            <Typography
                                variant="caption"
                                sx={{
                                    px: 2.5,
                                    py: 1,
                                    display: 'block',
                                    color: colors.sectionLabel,
                                    fontWeight: 600,
                                    fontSize: '0.65rem',
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                }}
                            >
                                {section.label}
                            </Typography>
                        )}
                        <List disablePadding>
                            {section.items.map((item) => {
                                const active = isActive(item);
                                const expanded = expandedMenu === item.title;
                                const hasChildren = !!item.children && item.children.length > 0;

                                return (
                                    <Box key={item.title}>
                                        <Tooltip
                                            title={!drawerOpen && isDesktop ? item.title : ''}
                                            placement="right"
                                            arrow
                                        >
                                            <ListItemButton
                                                onClick={() => handleItemClick(item)}
                                                sx={{
                                                    mx: 1,
                                                    mb: 0.25,
                                                    borderRadius: '8px',
                                                    py: 0.75,
                                                    px: isDesktop && !drawerOpen ? 1.5 : 1.5,
                                                    justifyContent: isDesktop && !drawerOpen ? 'center' : 'initial',
                                                    bgcolor: active ? colors.activeBg : 'transparent',
                                                    borderLeft: active ? `3px solid ${colors.active}` : '3px solid transparent',
                                                    '&:hover': {
                                                        bgcolor: active ? colors.activeBg : 'rgba(148, 163, 184, 0.08)',
                                                    },
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        minWidth: 0,
                                                        mr: drawerOpen || !isDesktop ? 1.5 : 0,
                                                        justifyContent: 'center',
                                                        color: active ? colors.active : colors.text,
                                                        '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                                                    }}
                                                >
                                                    {item.icon}
                                                </ListItemIcon>
                                                {(drawerOpen || !isDesktop) && (
                                                    <>
                                                        <ListItemText
                                                            primary={item.title}
                                                            primaryTypographyProps={{
                                                                fontSize: '0.8125rem',
                                                                fontWeight: active ? 600 : 400,
                                                                color: active ? '#fff' : colors.text,
                                                            }}
                                                        />
                                                        {hasChildren && (
                                                            expanded
                                                                ? <ExpandLess sx={{ color: colors.text, fontSize: '1.1rem' }} />
                                                                : <ExpandMore sx={{ color: colors.text, fontSize: '1.1rem' }} />
                                                        )}
                                                    </>
                                                )}
                                            </ListItemButton>
                                        </Tooltip>

                                        {hasChildren && (drawerOpen || !isDesktop) && (
                                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                                                <List disablePadding>
                                                    {item.children!.map((child) => {
                                                        const childActive = isChildActive(child.path);
                                                        return (
                                                            <ListItemButton
                                                                key={child.path}
                                                                onClick={() => {
                                                                    navigate(child.path);
                                                                    setMobileOpen(false);
                                                                }}
                                                                sx={{
                                                                    mx: 1,
                                                                    mb: 0.25,
                                                                    borderRadius: '8px',
                                                                    py: 0.5,
                                                                    pl: 5.5,
                                                                    '&:hover': {
                                                                        bgcolor: 'rgba(148, 163, 184, 0.08)',
                                                                    },
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    primary={child.title}
                                                                    primaryTypographyProps={{
                                                                        fontSize: '0.8125rem',
                                                                        fontWeight: childActive ? 600 : 400,
                                                                        color: childActive ? colors.active : colors.text,
                                                                    }}
                                                                />
                                                            </ListItemButton>
                                                        );
                                                    })}
                                                </List>
                                            </Collapse>
                                        )}
                                    </Box>
                                );
                            })}
                        </List>
                    </Box>
                ))}
            </Box>
        </Box>
    );

    const currentDrawerWidth = drawerOpen ? drawerWidth : drawerCollapsedWidth;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* Mobile menu button */}
            <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerToggle}
                sx={{
                    display: { xs: 'flex', sm: 'none' },
                    position: 'fixed',
                    top: 12,
                    left: 12,
                    zIndex: 1300,
                    bgcolor: colors.bg,
                    color: '#fff',
                    '&:hover': { bgcolor: '#243447' },
                    width: 40,
                    height: 40,
                }}
            >
                <MenuIcon fontSize="small" />
            </IconButton>

            {/* Nav */}
            <Box
                component="nav"
                sx={{
                    width: { sm: currentDrawerWidth },
                    flexShrink: { sm: 0 },
                    transition: (theme) =>
                        theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                }}
            >
                {/* Mobile drawer */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            bgcolor: colors.bg,
                            borderRight: 'none',
                        },
                    }}
                >
                    {drawerContent(false)}
                </Drawer>
                {/* Desktop drawer */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: currentDrawerWidth,
                            bgcolor: colors.bg,
                            borderRight: 'none',
                            transition: (theme) =>
                                theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration: theme.transitions.duration.enteringScreen,
                                }),
                            overflowX: 'hidden',
                        },
                    }}
                    open
                >
                    {drawerContent(true)}
                </Drawer>
            </Box>

            {/* Main content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minHeight: '100vh',
                    bgcolor: 'background.default',
                    width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
                    transition: (theme) =>
                        theme.transitions.create(['margin', 'width'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                }}
            >
                {children}
            </Box>
        </Box>
    );
};
